import { ExtraBase } from '~/lib/api/smartsend/types';
import { IAppSettings } from '../consts/settings-cache';
import { SmartSendProvider } from '../consts/providers';
import { ProtectionExtraIds } from '../consts/extras';
import { RoutingConstants } from '~/apps/smartsend/lib/consts/RoutingConstants';
import { Feature, SanitisedFeatureMapping } from '~/apps/smartsend/lib/types/Feature';
import { isTestOrStagingDomain } from '~/apps/smartsend/components/onboarding/testing/isTestDomain';

export function GetExtrasFromSettings(cache: IAppSettings): ExtraBase[] {
  return (
    cache?.Features?.filter((x) => x.startsWith('Extra:')).map((e) => {
      return {
        Id: e.split(':')[1],
        Provider: SmartSendProvider
      } as ExtraBase;
    }) || []
  );
}

export function PolyfillFeatures(x: IAppSettings, tenant: string) {
  const extras = GetExtrasFromSettings(x);

  if (extras.find((e) => ProtectionExtraIds.includes(e.Id))) {
    x.Features?.push('Protection');
  }

  if (extras.find((e) => !ProtectionExtraIds.includes(e.Id))) {
    x.Features?.push('Extras');
  }

  // Test Purposes
  if (x.Features && isTestOrStagingDomain() && window.location.search.includes('features')) {
    const urlParams = new URLSearchParams(window.location.search);
    const features = urlParams.get('features')?.split(',') || [];
    features.forEach((f) => {
      if (!x.Features.includes(f as Feature)) {
        x.Features.push(f as Feature);
      }
    });
  }

  // Handle sanitised feature tags from config and map to local feature names
  for (const sanitisedFeature in SanitisedFeatureMapping) {
    const destinationFeature = SanitisedFeatureMapping[sanitisedFeature];
    if (x.Features.includes(sanitisedFeature as Feature) && !x.Features.includes(destinationFeature)) {
      x.Features.push(destinationFeature);
    }
  }

  // Remove sanitised feature tags from local features
  x.Features = x.Features.filter((x) => !Object.keys(SanitisedFeatureMapping).includes(x));

  if (x.SmartSend) {
    // Main Dash
    x.SmartSend.Home = RoutingConstants.SmartSend;

    for (const key in x.SmartSend) {
      switch (key) {
        case 'SoldHash':
          x.Features.push('Link:SoldItems');
          delete x.SmartSend[key];
          break;

        case 'BookedHash':
          x.Features.push('Link:BookedItems');
          delete x.SmartSend[key];
          break;

        case 'ManifestsHash':
          x.Features.push('Manifests');
          delete x.SmartSend[key];
          break;

        case 'PackageContentHash':
          delete x.SmartSend[key];
          break;

        case 'StatementHash':
          x.Features.push('Link:Statements');
          delete x.SmartSend[key];
          break;

        case 'AccountConcernsHash':
          x.Features.push('Dash:AccountConcerns');
          x.Features.push('Link:AccountConcerns');
          delete x.SmartSend[key];
          break;

        case 'CsvMappingHash':
          delete x.SmartSend[key];
          break;

        case 'PricingMatrixHash':
          delete x.SmartSend[key];
          break;

        case 'InTransitHash':
          x.Features.push('Link:InTransit');
          delete x.SmartSend[key];
          break;

        case 'AwaitingDispatchHash':
          x.Features.push('Link:AwaitingDispatch');
          delete x.SmartSend[key];
          break;
      }
    }
  }

  if (x.Downloads.SampleDomesticCsv || x.Downloads.SampleInternationalCsv) {
    x.Features.push('CsvUploadExamples');
  }

  // Potentially remove in the future if Rates becomes a fully fledged feature with its own settings and isn't just a sidebar link gate.
  if (tenant?.toUpperCase().startsWith('ASENDIA')) {
    // Adding a SaveAddressOnSenderUpdate feature for Asendia Spain.
    // They use TaxIds to this helps with mass update.
    x.Features.push('SaveAddressOnSenderUpdate');
    x.Features.push('UpdateCustomerOnSenderUpdate');
    x.Features.push('PreBookingVerification');

    // Adding a Rates feature for Asendia to enable a sidebar link.
    if (x.Features.includes('Prices') && !x.Features.includes('Rates')) {
      x.Features.push('Rates');
    }
  }

  // Consignments on as default, unless disabled via ForceSingleParcelConsignments
  if (!x.Features.includes('ForceSingleParcelConsignments') && !x.Features.includes('Consignments')) {
    x.Features.push('Consignments');
  }

  // If Consignments is enabled, ParcelSplitting must also be enabled to allow for multiple parcels in a consignment.
  if (x.Features.includes('Consignments') && !x.Features.includes('ParcelSplitting')) {
    x.Features.push('ParcelSplitting');
  }
}
